import {
    TokenProvider,
    decodeJWT
} from 'aws-amplify/auth';

// Note: This example requires installing `@aws-sdk/client-cognito-identity` to obtain Cognito credentials
// npm i @aws-sdk/client-cognito-identity
import { CognitoIdentity } from '@aws-sdk/client-cognito-identity';
import { environment } from 'src/environments/environment';

// You can make use of the sdk to get identityId and credentials
const cognitoidentity = new CognitoIdentity({
    region: environment.cognito.region,
});


export interface FederatedLogin {
    domain: string;
    token: string;
    username?: string; // Store username for Identity Pool users
}

export interface EdisonFedertedLogin extends FederatedLogin {
    edisonDomain?: string;
    edisonRefreshToken?: string;
}

const FEDERATED_LOGIN_STORAGE_KEY = 'ktd_federated_login';

export class CommonTokenProvider implements TokenProvider {
    constructor(private tokenProvider: TokenProvider) {
        // Load federated login from localStorage on initialization
        this.loadFederatedLoginFromStorage();
    }

    federatedLogin?: FederatedLogin | EdisonFedertedLogin;

    // Load federated login from localStorage
    private loadFederatedLoginFromStorage() {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const stored = localStorage.getItem(FEDERATED_LOGIN_STORAGE_KEY);
                if (stored) {
                    try {
                        this.federatedLogin = JSON.parse(stored);
                        console.log('Federated login loaded from localStorage:', this.federatedLogin ? 'success' : 'failed');
                    } catch (parseError) {
                        console.error('Error parsing federated login from localStorage:', parseError);
                        // Clear invalid data
                        localStorage.removeItem(FEDERATED_LOGIN_STORAGE_KEY);
                    }
                } else {
                    console.log('No federated login found in localStorage');
                }
            } else {
                console.warn('localStorage not available');
            }
        } catch (error) {
            console.error('Error loading federated login from localStorage:', error);
        }
    }

    // Save federated login to localStorage
    private saveFederatedLoginToStorage(login?: FederatedLogin | EdisonFedertedLogin) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                if (login) {
                    localStorage.setItem(FEDERATED_LOGIN_STORAGE_KEY, JSON.stringify(login));
                } else {
                    localStorage.removeItem(FEDERATED_LOGIN_STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error('Error saving federated login to localStorage:', error);
        }
    }

    // Clear federated login from localStorage
    clearFederatedLogin() {
        this.federatedLogin = undefined;
        this.saveFederatedLoginToStorage(undefined);
    }

    // Reload federated login from localStorage (useful if it was cleared from memory)
    reloadFederatedLoginFromStorage() {
        // Clear current value first
        this.federatedLogin = undefined;
        // Then reload
        this.loadFederatedLoginFromStorage();
        // Log for debugging
        if (this.federatedLogin) {
            console.log('Federated login reloaded from localStorage');
        } else {
            console.log('No federated login found in localStorage');
        }
        return this.federatedLogin;
    }
    
    // Check if federated login exists (in memory or localStorage)
    hasFederatedLogin(): boolean {
        if (this.federatedLogin && this.federatedLogin.token) {
            return true;
        }
        // Try to reload from storage
        this.reloadFederatedLoginFromStorage();
        return !!(this.federatedLogin && this.federatedLogin.token);
    }

    // Custom method to load the federated login information
    loadFederatedLogin(login?: typeof this.federatedLogin) {
        this.federatedLogin = login;
        // Persist to localStorage
        this.saveFederatedLoginToStorage(login);
    }

    async getTokens({ forceRefresh }) {
        if (forceRefresh) {
            // try to obtain new tokens if possible
        }

        if (this.federatedLogin && this.federatedLogin.token) {
            const accessTokenString = this.federatedLogin.token;
            const decoded = decodeJWT(accessTokenString);

            // Check if token is expired
            if (decoded.payload && decoded.payload.exp) {
                const expirationTime = decoded.payload.exp * 1000; // Convert to milliseconds
                const currentTime = Date.now();
                if (currentTime >= expirationTime) {
                    // Token is expired, clear it and fall back to regular token provider
                    console.warn('Federated login token has expired, clearing from storage');
                    this.clearFederatedLogin();
                    return this.tokenProvider.getTokens({ forceRefresh });
                }
            }

            return {
                accessToken: decoded,
            };
        }

        return this.tokenProvider.getTokens({ forceRefresh });
    }
};